import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { GlobalSettings } from './components/GlobalSettings';
import { CustomerDataTable } from './components/CustomerDataTable';
import { JsonTransformer } from './components/JsonTransformer';
import { MetricsTab } from './components/MetricsTab';
import { Customer } from './types/types';
import { apiService, SessionExpiredError } from './services/api';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import Tooltip from '@mui/material/Tooltip';
import { useAuth } from './auth/AuthContext';
import { LoginPage, APP_TITLE } from './auth/LoginPage';
import { UserMenu } from './auth/UserMenu';
import { ChangePasswordPage } from './auth/ChangePasswordPage';
import { UserManagementPage } from './auth/UserManagementPage';
import { AuditLogPage } from './auth/AuditLogPage';
import './auth/auth.css';
import './App.css';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <div className="tab-content">{children}</div>}
    </div>
  );
}

function App() {
  const { session, logout } = useAuth();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [appVersion, setAppVersion] = useState<string | null>(null);
  const scrollPositionRef = useRef<number>(0);
  const isAdmin = session?.role === 'ADMIN';
  const metricsTabIndex = isAdmin ? 4 : 2;

  useLayoutEffect(() => {
    if (scrollPositionRef.current > 0) {
      window.scrollTo(0, scrollPositionRef.current);
    }
  }, [customers]);

  const fetchData = async () => {
    scrollPositionRef.current = window.scrollY;
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiService.getExtendedConfig();
      if (data && data.customers) {
        setCustomers(data.customers);
        setAppVersion(data.appVersion ?? null);
      } else {
        setError('Invalid configuration data received');
      }
    } catch (err) {
      if (err instanceof SessionExpiredError) return;
      console.error('Fetch error:', err);
      setError('Failed to load configuration.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setTabValue(0);
  }, [session?.username, session?.role]);

  useEffect(() => {
    if (!session || session.mustChangePassword) return;
    const initialFetch = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await apiService.getExtendedConfig();
        if (data && data.customers) {
          setCustomers(data.customers);
          setAppVersion(data.appVersion ?? null);
        } else {
          setError('Invalid configuration data received');
        }
      } catch (err) {
        if (err instanceof SessionExpiredError) return;
        console.error('Fetch error:', err);
        setError('Failed to load configuration.');
      } finally {
        setIsLoading(false);
      }
    };
    initialFetch();
  }, [session]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (message: string, type: 'success' | 'error') => {
    setNotification({ message, type });
  };

  const handleDeleteCustomer = async (apikey: string) => {
    try {
      await apiService.removeCustomer(apikey);
      await fetchData();
      showNotification('Customer deleted successfully', 'success');
    } catch (err) {
      console.error('Delete error:', err);
      showNotification('Failed to delete customer', 'error');
    }
  };

  const handleAddCustomer = async (customer: Customer) => {
    try {
      await apiService.postConfig({ customers: [customer] });
      await fetchData();
      showNotification('Customer added successfully', 'success');
    } catch (err) {
      console.error('Add error:', err);
      showNotification('Failed to add customer', 'error');
      throw err;
    }
  };

  const handleEditCustomer = async (
    apikey: string,
    changes: Partial<Pick<Customer, 'readBatchSize' | 'readDelayMillis' | 'postURL'>>
  ) => {
    try {
      await apiService.postConfig({
        customers: [{ apikey, ...changes }],
      });
      await fetchData();
      showNotification('Customer settings updated successfully', 'success');
    } catch (err) {
      console.error('Edit error:', err);
      showNotification('Failed to update customer settings', 'error');
      throw err;
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (!session) {
    return <LoginPage />;
  }

  if (session.mustChangePassword || showChangePassword) {
    return <ChangePasswordPage onDone={() => setShowChangePassword(false)} />;
  }

  if (error) {
    return (
      <div className="error-message">
        <h3>Error</h3>
        <p>{error}</p>
        <button onClick={() => fetchData()} className="button primary">
          Retry
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="loading-message">
        <h3>Loading Configuration...</h3>
        <p>Please wait while we fetch the settings...</p>
      </div>
    );
  }

  return (
    <div className="app-container">
      <header className="app-header">
        <div className="app-title">
          <h1>{APP_TITLE}</h1>
          {appVersion && <div className="app-version">Version {appVersion}</div>}
        </div>
        <div className="app-header-actions">
          <UserMenu
            username={session.username}
            role={session.role}
            onChangePassword={() => setShowChangePassword(true)}
            onLogout={logout}
          />
          {tabValue === 0 && (
            <Tooltip title="Refresh Data" arrow>
              <button
                type="button"
                className="action-button refresh-app"
                onClick={() => fetchData()}
                disabled={isLoading}
                aria-label="Refresh configuration"
              >
                <RefreshOutlinedIcon />
              </button>
            </Tooltip>
          )}
        </div>
      </header>

      {notification && (
        <div className={`notification app-notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      <div className="tabs-container">
        <div className="tabs-header">
          <button
            className={`tab-button ${tabValue === 0 ? 'active' : ''}`}
            onClick={(e) => handleTabChange(e, 0)}
            id="simple-tab-0"
            aria-controls="simple-tabpanel-0"
            aria-selected={tabValue === 0}
          >
            Configuration
          </button>
          <button
            className={`tab-button ${tabValue === 1 ? 'active' : ''}`}
            onClick={(e) => handleTabChange(e, 1)}
            id="simple-tab-1"
            aria-controls="simple-tabpanel-1"
            aria-selected={tabValue === 1}
          >
            Transformer Utility
          </button>
          {isAdmin && (
            <>
              <button
                className={`tab-button ${tabValue === 2 ? 'active' : ''}`}
                onClick={(e) => handleTabChange(e, 2)}
                id="simple-tab-2"
                aria-controls="simple-tabpanel-2"
                aria-selected={tabValue === 2}
              >
                User Management
              </button>
              <button
                className={`tab-button ${tabValue === 3 ? 'active' : ''}`}
                onClick={(e) => handleTabChange(e, 3)}
                id="simple-tab-3"
                aria-controls="simple-tabpanel-3"
                aria-selected={tabValue === 3}
              >
                Audit log
              </button>
            </>
          )}
          <button
            className={`tab-button ${tabValue === metricsTabIndex ? 'active' : ''}`}
            onClick={(e) => handleTabChange(e, metricsTabIndex)}
            id={`simple-tab-${metricsTabIndex}`}
            aria-controls={`simple-tabpanel-${metricsTabIndex}`}
            aria-selected={tabValue === metricsTabIndex}
          >
            Metrics
          </button>
        </div>

        <TabPanel value={tabValue} index={0}>
          <div className="configuration-panel">
            <GlobalSettings />
            <CustomerDataTable
              customers={customers}
              onDelete={handleDeleteCustomer}
              onEdit={handleEditCustomer}
              onAdd={handleAddCustomer}
              onRefresh={fetchData}
              showNotification={showNotification}
            />
          </div>
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <div className="logs-panel">
            <JsonTransformer />
          </div>
        </TabPanel>
        {isAdmin && (
          <>
            <TabPanel value={tabValue} index={2}>
              <UserManagementPage />
            </TabPanel>
            <TabPanel value={tabValue} index={3}>
              <AuditLogPage />
            </TabPanel>
          </>
        )}
        <TabPanel value={tabValue} index={metricsTabIndex}>
          <div className="metrics-panel">
            <MetricsTab active={tabValue === metricsTabIndex} />
          </div>
        </TabPanel>
      </div>
    </div>
  );
}

export default App;
